// Pure, framework-free logic for the one-time passcode (OTP) that guards a public
// sign-up's mobile number. The code itself is generated and hashed server-side
// (secure randomness / keyed hashing are not domain concerns); this module owns
// the shared, testable rules around the challenge: its shape, its lifetime and
// the brute-force attempt budget. Kept import-free so it is shared by the API
// server (which issues and verifies codes) and the public form (which knows the
// code length) alike.

import type { OtpChallenge } from './types';

/** Number of decimal digits in a delivered passcode. */
export const OTP_CODE_LENGTH = 6;

/** How long a delivered code stays valid (10 minutes). */
export const OTP_TTL_MS = 10 * 60 * 1000;

/** Maximum failed verification attempts before a challenge is locked out. */
export const OTP_MAX_ATTEMPTS = 5;

/** Whether `code` is exactly the expected number of decimal digits. */
export function isOtpCodeFormat(code: string): boolean {
  return new RegExp(`^\\d{${OTP_CODE_LENGTH}}$`).test(code);
}

/**
 * Build a fresh OTP challenge for `registrationId` from an already-computed
 * `codeHash`. `now` (epoch ms) is injectable for deterministic tests and sets
 * both `createdAt` and the `expiresAt = now + OTP_TTL_MS` deadline.
 */
export function createOtpChallenge(
  registrationId: string,
  codeHash: string,
  now: number = Date.now(),
): OtpChallenge {
  return {
    registrationId,
    codeHash,
    expiresAt: now + OTP_TTL_MS,
    attempts: 0,
    createdAt: now,
  };
}

/** Whether the challenge's code has passed its expiry. */
export function isOtpExpired(
  challenge: Pick<OtpChallenge, 'expiresAt'>,
  now: number = Date.now(),
): boolean {
  return now >= challenge.expiresAt;
}

/** Whether the challenge has any verification attempts left in its budget. */
export function hasOtpAttemptsLeft(
  challenge: Pick<OtpChallenge, 'attempts'>,
): boolean {
  return challenge.attempts < OTP_MAX_ATTEMPTS;
}
